import helpers from './helpers';
import { v4 as uuidv4 } from 'uuid';

const BEACON_API_URL = 'https://beacon.searchspring.io/beacon';

// Track impressions to prevent duplicates
const trackedImpressions = new Set();
const trackedProfiles = new Set();

// Track if beacon is blocked (ad blocker)
let isBeaconBlocked = false;
let hasCheckedBeacon = false;

// Robust getters for SS IDs from localStorage or cookies
const readStorage = (keys = []) => {
  for (const k of keys) {
    try {
      const ls = window.localStorage.getItem(k);
      if (ls) return ls;
    } catch (_) {}
    try {
      const m = document.cookie.match(new RegExp(`${k}=([^;]+)`));
      if (m && m[1]) return m[1];
    } catch (_) {}
  }
  return '';
};

/**
 * Check if beacon endpoint is accessible (not blocked by ad blocker)
 */
const checkBeaconAccess = async () => {
  if (hasCheckedBeacon) return !isBeaconBlocked;
  
  // Assume accessible unless we get a definitive block error
  hasCheckedBeacon = true;
  isBeaconBlocked = false;
  return true;
};

/**
 * Get common beacon parameters
 */
const getBeaconParams = () => {
  const siteId = window.theme?.ssSiteId;
  const userId = helpers.getSSCookieId('ssUserId')
    || readStorage(['ssUserId','ss-user-id']);
  const sessionId = helpers.getSSCookieId('ssSessionId')
    || helpers.getSSCookieId('ssSessionIdNamespace')
    || readStorage(['ssSessionId','ss-session-id','ssSessionIdNamespace']);

  return {
    siteId,
    userId,
    sessionId,
    pageLoadId: uuidv4(),
    // Use the public-facing hostname, not myshopify/admin
    domain: window.location.hostname,
    href: window.location.href
  };
};

/**
 * Send beacon event to SearchSpring with proper error handling
 */
export const sendBeaconEvent = async (eventData) => {
  // Skip the pre-check, just try to send
  try {
    const baseParams = getBeaconParams();

    const payload = {
      type: eventData.type,
      category: 'searchspring.recommendations.user-interactions',
      trackingCode: baseParams.siteId,
      event: {
        ...(eventData.profile && { profile: eventData.profile }),
        ...(eventData.product && { product: eventData.product }),
        context: {
          siteId: baseParams.siteId,
          // Provide both camelCase and snake_case for SS ingestion
          userId: baseParams.userId,
          sessionId: baseParams.sessionId,
          user_id: baseParams.userId,
          session_id: baseParams.sessionId,
          pageLoadId: baseParams.pageLoadId,
          domain: window.location.hostname,
          href: window.location.href,
          timestamp: new Date().toISOString(),
          type: 'product-recommendation',
          ...eventData.context,
        }
      }
    };

    // Only log in development or when debugging
    if (window.location.hostname === 'localhost' || window.location.hostname.includes('dev') || window.location.search.includes('debug=true')) {
      console.log('Sending Beacon Event:', JSON.stringify(payload, null, 2));
    }

    const response = await fetch(BEACON_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Beacon API error ${response.status}:`, errorText);
      throw new Error(`Beacon API error: ${response.status} - ${errorText}`);
    }

    console.log('Beacon event sent successfully');
    return response;
    
  } catch (error) {
    if (error.message.includes('net::ERR_BLOCKED_BY_CLIENT') || 
        error.message.includes('net::ERR_NETWORK_ACCESS_DENIED')) {
      isBeaconBlocked = true;
      // Only log in development
      if (window.location.hostname === 'localhost' || window.location.hostname.includes('dev')) {
        console.warn('Beacon blocked by ad blocker - tracking disabled');
      }
    } else {
      console.error('Error sending beacon event:', error);
    }
    return null;
  }
};

/**
 * Create impression tracking key to prevent duplicates
 */
const createImpressionKey = (product, context, additionalData = {}) => {
  const productId = product.id?.toString() || product.uid?.toString();
  const position = additionalData.position || 0;
  const profile = additionalData.recommendationProfile || '';
  return `${productId}-${context}-${position}-${profile}`;
};

/**
 * Create profile tracking key to prevent duplicate profile renders
 */
const createProfileKey = (profile, contextProductId = '') => {
  return `${profile}-${contextProductId}`;
};

/**
 * Track profile render event (when recommendation section loads)
 */
export const trackProfileRender = (profile, additionalData = {}) => {
  const profileKey = createProfileKey(profile, additionalData.contextProductId);
  
  if (trackedProfiles.has(profileKey)) {
    // Reduce noise - only log in debug mode
    if (window.location.hostname === 'localhost' || window.location.search.includes('debug=true')) {
      console.log(`Skipping duplicate profile render: ${profileKey}`);
    }
    return;
  }

  trackedProfiles.add(profileKey);

  const eventData = {
    type: 'profile.render',
    profile: {
      tag: profile
    },
    context: {
      type: 'product-recommendation',
      placement: 'recommendation',
      ...additionalData
    }
  };

  console.log(`Tracking profile render: ${profile}`);
  return sendBeaconEvent(eventData);
};

/**
 * Track product impression with deduplication
 */
export const trackProductImpression = (product, context = 'unknown', additionalData = {}) => {
  const productId = product.id?.toString() || product.uid?.toString();
  
  if (!productId) {
    console.warn('Skipping impression: missing product ID');
    return;
  }

  // Create unique key for this impression
  const impressionKey = createImpressionKey(product, context, additionalData);
  
  if (trackedImpressions.has(impressionKey)) {
    // Reduce noise - only log in debug mode
    if (window.location.hostname === 'localhost' || window.location.search.includes('debug=true')) {
      console.log(`Skipping duplicate impression: ${impressionKey}`);
    }
    return;
  }

  trackedImpressions.add(impressionKey);
  
  const eventData = {
    type: 'profile.product.impression',
    product: {
      id: productId,
      mappings: {
        core: {
          uid: productId,
          sku: product.variants?.[0]?.sku || product.sku || '',
          name: product.title || product.name || '',
          price: parseFloat(product.price || product.variants?.[0]?.price || 0),
          msrp: parseFloat(product.compare_at_price || product.variants?.[0]?.compare_at_price || 0),
          imageUrl: product.featured_image || product.images?.[0]?.src || ''
        }
      }
    },
    ...(additionalData.recommendationProfile && {
      profile: {
        tag: additionalData.recommendationProfile
      }
    }),
    context: {
      type: 'product-recommendation',
      placement: context,
      ...additionalData
    }
  };

  // Only log individual impressions in debug mode
  if (window.location.hostname === 'localhost' || window.location.search.includes('debug=true')) {
    console.log(`Tracking impression: ${impressionKey}`);
  }
  
  return sendBeaconEvent(eventData);
};

/**
 * Track product click (immediate - don't batch clicks)
 */
export const trackProductClick = async (product, context = 'unknown', additionalData = {}) => {
  const productId = product.id?.toString() || product.uid?.toString();
  
  if (!productId) {
    console.warn('Skipping click: missing product ID');
    return;
  }

  const eventData = {
    type: 'profile.product.click',
    product: {
      id: productId,
      mappings: {
        core: {
          uid: productId,
          sku: product.variants?.[0]?.sku || product.sku || '',
          name: product.title || product.name || '',
          price: parseFloat(product.price || product.variants?.[0]?.price || 0),
          msrp: parseFloat(product.compare_at_price || product.variants?.[0]?.compare_at_price || 0),
          imageUrl: product.featured_image || product.images?.[0]?.src || ''
        }
      }
    },
    ...(additionalData.recommendationProfile && {
      profile: {
        tag: additionalData.recommendationProfile
      }
    }),
    context: {
      type: 'product-recommendation',
      placement: context,
      position: additionalData.position || 0,
      ...additionalData
    }
  };

  console.log(`Tracking click: ${productId} in ${context}`);
  return sendBeaconEvent(eventData);
};

/**
 * Track search query (non-recommendation events)
 */
export const trackSearch = (query, totalResults = 0, additionalData = {}) => {
  const eventData = {
    type: 'search.query',
    context: {
      type: 'search',
      query,
      totalResults,
      ...additionalData
    }
  };

  return sendBeaconEvent(eventData);
};

/**
 * Track autocomplete interaction (non-recommendation events)
 */
export const trackAutocomplete = (query, suggestion, additionalData = {}) => {
  const eventData = {
    type: 'search.autocomplete',
    context: {
      type: 'autocomplete',
      query,
      suggestion,
      ...additionalData
    }
  };

  return sendBeaconEvent(eventData);
};

/**
 * Track recommendation impression with profile render and product impressions
 */
export const trackRecommendationImpression = (products, profile, additionalData = {}) => {
  if (!products || products.length === 0) {
    console.warn('No products to track for recommendations');
    return;
  }

  // Check if we've already tracked this profile render
  const profileKey = createProfileKey(profile, additionalData.contextProductId);
  const hasTrackedProfile = trackedProfiles.has(profileKey);

  // Count how many products would actually be tracked (not duplicates)
  const newImpressions = products.filter((product, index) => {
    const impressionKey = createImpressionKey(product, 'recommendation', {
      recommendationProfile: profile,
      position: index + 1,
      ...additionalData
    });
    return !trackedImpressions.has(impressionKey);
  });

  if (newImpressions.length === 0 && hasTrackedProfile) {
    // Only log in debug mode to reduce noise
    if (window.location.hostname === 'localhost' || window.location.search.includes('debug=true')) {
      console.log(`All impressions already tracked for profile: ${profile}`);
    }
    return;
  }

  // Only log if there are actually new impressions to track
  if (newImpressions.length > 0) {
    console.log(`Tracking ${newImpressions.length} new impressions for profile: ${profile}`);
  }
  
  // Track the profile render event (with deduplication)
  trackProfileRender(profile, additionalData);
  
  // Track individual product impressions (with deduplication)
  products.forEach((product, index) => {
    trackProductImpression(product, 'recommendation', {
      recommendationProfile: profile,
      position: index + 1,
      ...additionalData
    });
  });
};

/**
 * Track IntelliSuggest events (same as trackProductImpression/Click but with specific context)
 */
export const trackIntelliSuggest = (type, product, profile, additionalData = {}) => {
  if (type === 'impression') {
    return trackProductImpression(product, 'intellisuggest', {
      recommendationProfile: profile,
      ...additionalData
    });
  } else if (type === 'click') {
    return trackProductClick(product, 'intellisuggest', {
      recommendationProfile: profile,
      ...additionalData
    });
  } else if (type === 'profile.render') {
    return trackProfileRender(profile, {
      placement: 'intellisuggest',
      ...additionalData
    });
  }
};

/**
 * Track IntelliSuggest impression specifically
 */
export const trackIntelliSuggestImpression = (product, profile, additionalData = {}) => {
  return trackProductImpression(product, 'intellisuggest', {
    recommendationProfile: profile,
    ...additionalData
  });
};

/**
 * Track IntelliSuggest click specifically
 */
export const trackIntelliSuggestClick = (product, profile, additionalData = {}) => {
  return trackProductClick(product, 'intellisuggest', {
    recommendationProfile: profile,
    ...additionalData
  });
};

/**
 * Clear impression tracking (call this on page navigation)
 */
export const clearImpressionTracking = () => {
  trackedImpressions.clear();
  trackedProfiles.clear();
  isBeaconBlocked = false;
  hasCheckedBeacon = false;
  console.log('Impression tracking cleared');
};

export default {
  sendBeaconEvent,
  trackProfileRender,
  trackProductImpression,
  trackProductClick,
  trackSearch,
  trackAutocomplete,
  trackRecommendationImpression,
  trackIntelliSuggest,
  trackIntelliSuggestImpression,
  trackIntelliSuggestClick,
  clearImpressionTracking
};